import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Eye, EyeOff, KeyRound, Loader2, LogOut, ShieldCheck, User, X } from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  changeAccountPassword,
  loadAccountProfile,
  updateAccountProfile,
  type AccountProfile,
} from '../services/account';
import type { AuthUser } from '../services/auth';

type AccountSection = 'profile' | 'security';

interface AccountCenterProps {
  isOpen: boolean;
  initialSection?: AccountSection;
  authUser?: AuthUser | null;
  onClose: () => void;
  onUserUpdated?: (user: AuthUser) => void;
  onLogout?: () => void;
}

function isEmailLike(value?: string): boolean {
  return Boolean(value?.includes('@'));
}

function isPlanLikeName(value?: string): boolean {
  return /^vip[\s_-]*\d+$/i.test((value || '').trim());
}

function sanitizePersonName(value?: string): string {
  const trimmed = value?.trim() || '';
  if (!trimmed || isEmailLike(trimmed) || isPlanLikeName(trimmed)) {
    return '';
  }
  return trimmed;
}

function getEmailPrefix(email?: string): string {
  return (email || '').split('@')[0]?.trim() || '';
}

function getAccountName(account: AccountProfile | null, authUser?: AuthUser | null): string {
  return (
    account?.username?.trim()
    || authUser?.username?.trim()
    || sanitizePersonName(account?.name)
    || sanitizePersonName([account?.lastName, account?.firstName].filter(Boolean).join(''))
    || sanitizePersonName(authUser?.name)
  );
}

function getDisplayName(account: AccountProfile | null, authUser?: AuthUser | null): string {
  const userKey = account?.email || authUser?.email || 'default';
  const savedUserName = typeof localStorage === 'undefined' ? '' : localStorage.getItem(`user_name_${userKey}`) || '';
  return (
    sanitizePersonName(savedUserName)
    || getAccountName(account, authUser)
    || getEmailPrefix(account?.email || authUser?.email)
    || account?.username?.trim()
    || ''
  );
}

export const AccountCenter: React.FC<AccountCenterProps> = ({
  isOpen,
  initialSection = 'profile',
  authUser,
  onClose,
  onUserUpdated,
  onLogout,
}) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<AccountSection>(initialSection);
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [accountAvailable, setAccountAvailable] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [profileForm, setProfileForm] = useState({
    username: '',
    phone: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setActiveSection(initialSection);
    setIsLoading(true);
    setLoadError(null);
    setProfileMessage(null);
    setProfileError(null);
    setPasswordMessage(null);
    setPasswordError(null);

    void loadAccountProfile()
      .then((data) => {
        const nextAccount = data.account;
        setAccount(nextAccount);
        setProfileForm({
          username: nextAccount.username || authUser?.username || '',
          phone: nextAccount.phone || authUser?.phone || '',
        });
        setAccountAvailable(data.accountManagementAvailable !== false);
        setLoadError(data.accountManagementMessage || null);
      })
      .catch((error) => {
        setLoadError(error instanceof Error ? error.message : t('Failed to load account profile'));
      })
      .finally(() => setIsLoading(false));
  }, [authUser?.email, authUser?.name, initialSection, isOpen, t]);

  const roleLabel = useMemo(() => {
    if (authUser?.role === 'super_admin') return t('Platform Admin');
    if (authUser?.role === 'admin') return t('Organization Admin');
    if (authUser?.role === 'user') return t('Authenticated User');
    return t('Viewer');
  }, [authUser?.role, t]);

  const validateProfileForm = () => {
    const username = profileForm.username.trim();
    const phone = profileForm.phone.trim();

    if (!username) {
      return t('Username is required');
    }
    if (/[^\x00-\x7F]/.test(username)) {
      return t('Username cannot contain Chinese characters');
    }
    if (!/^[A-Za-z0-9._@+-]{3,255}$/.test(username)) {
      return t('Username rule hint');
    }
    if (phone && !/^[0-9+()\-\s]{1,32}$/.test(phone)) {
      return t('Phone rule hint');
    }
    return null;
  };

  const handleSaveProfile = async () => {
    const validationError = validateProfileForm();
    if (validationError) {
      setProfileError(validationError);
      setProfileMessage(null);
      return;
    }

    setIsSavingProfile(true);
    setProfileError(null);
    setProfileMessage(null);

    try {
      const result = await updateAccountProfile({
        username: profileForm.username.trim(),
        phone: profileForm.phone.trim(),
      });
      setAccount(result.account);
      setProfileForm({
        username: result.account.username || '',
        phone: result.account.phone || '',
      });
      if (result.user) {
        onUserUpdated?.(result.user);
      }
      setProfileMessage(t('Account profile updated'));
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : t('Failed to update account profile'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setIsChangingPassword(true);
    setPasswordError(null);
    setPasswordMessage(null);

    try {
      await changeAccountPassword(passwordForm);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordMessage(t('Password updated. Please sign in with the new password next time.'));
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : t('Failed to update password'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!isOpen) return null;

  const accountLabel = account?.username || authUser?.username || account?.email || authUser?.email || authUser?.name || t('Authenticated User');
  const accountName = getAccountName(account, authUser);
  const displayName = getDisplayName(account, authUser);
  const email = account?.email || authUser?.email || '';
  const loginRows = [
    { label: t('Username'), value: accountName },
    { label: t('Email'), value: email },
    { label: t('Role'), value: roleLabel },
  ];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-zinc-900/20 backdrop-blur-sm dark:bg-black/50">
      <div className="mx-4 flex h-[82vh] max-h-[820px] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-[#1e1e20]">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-5 dark:border-zinc-800/50">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">{t('Account Center')}</h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t('Current sign-in')}: {accountLabel}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 transition-colors hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300">
            <X size={20} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <aside className="border-b border-zinc-200 bg-zinc-50/70 dark:border-zinc-800/50 dark:bg-[#18181b] lg:w-64 lg:border-b-0 lg:border-r">
            <div className="flex gap-2 overflow-x-auto px-4 py-4 lg:flex-col">
              <button
                type="button"
                onClick={() => setActiveSection('profile')}
                className={`flex min-w-fit items-center gap-3 rounded-lg border px-3 py-3 text-left text-sm transition-colors lg:w-full ${
                  activeSection === 'profile'
                    ? 'border-cyan-500/50 bg-cyan-500/10 text-zinc-900 dark:text-zinc-100'
                    : 'border-transparent text-zinc-600 hover:bg-white dark:text-zinc-400 dark:hover:bg-zinc-900/60'
                }`}
              >
                <User size={18} className="text-cyan-600 dark:text-cyan-400" />
                <span>
                  <span className="block font-medium">{t('Account Profile')}</span>
                  <span className="mt-1 block text-xs text-zinc-400 dark:text-zinc-500">{roleLabel}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveSection('security')}
                className={`flex min-w-fit items-center gap-3 rounded-lg border px-3 py-3 text-left text-sm transition-colors lg:w-full ${
                  activeSection === 'security'
                    ? 'border-cyan-500/50 bg-cyan-500/10 text-zinc-900 dark:text-zinc-100'
                    : 'border-transparent text-zinc-600 hover:bg-white dark:text-zinc-400 dark:hover:bg-zinc-900/60'
                }`}
              >
                <ShieldCheck size={18} className="text-cyan-600 dark:text-cyan-400" />
                <span>
                  <span className="block font-medium">{t('Account Security')}</span>
                  <span className="mt-1 block text-xs text-zinc-400 dark:text-zinc-500">{t('Password and sign-in')}</span>
                </span>
              </button>
            </div>
          </aside>

          <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {isLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-zinc-500 dark:text-zinc-400">
                <Loader2 size={18} className="mr-2 animate-spin" />
                {t('Loading account profile')}
              </div>
            ) : (
              <div className="space-y-5">
                {loadError ? (
                  <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
                    accountAvailable
                      ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'
                      : 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'
                  }`}>
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{loadError}</span>
                  </div>
                ) : null}

                {activeSection === 'profile' ? (
                  <div className="space-y-5">
                    <InfoSection title={t('Login Account')} rows={loginRows} emptyText={t('Not set')} />
                    <section className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-4 dark:border-zinc-800 dark:bg-[#151515]">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Account Profile')}</h3>
                          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('Display Name')}: {displayName || t('Not set')}</p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <TextInput
                          label={t('Username')}
                          value={profileForm.username}
                          disabled={!accountAvailable || isSavingProfile}
                          onChange={(value) => setProfileForm((prev) => ({ ...prev, username: value }))}
                        />
                        <TextInput
                          label={t('Phone')}
                          value={profileForm.phone}
                          disabled={!accountAvailable || isSavingProfile}
                          onChange={(value) => setProfileForm((prev) => ({ ...prev, phone: value }))}
                        />
                      </div>

                      <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
                        {t('Username rule hint')}
                      </p>

                      <div className="mt-4 space-y-3">
                        {profileError ? <StatusMessage tone="error" text={profileError} /> : null}
                        {profileMessage ? <StatusMessage tone="success" text={profileMessage} /> : null}
                        <button
                          type="button"
                          onClick={handleSaveProfile}
                          disabled={!accountAvailable || isSavingProfile}
                          className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
                        >
                          {isSavingProfile ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                          {t('Save Account Profile')}
                        </button>
                      </div>
                    </section>
                  </div>
                ) : null}

                {activeSection === 'security' ? (
                  <div className="space-y-5">
                    <div className="grid gap-4">
                      <PasswordInput
                        label={t('Current Password')}
                        value={passwordForm.currentPassword}
                        disabled={!accountAvailable}
                        onChange={(value) => setPasswordForm((prev) => ({ ...prev, currentPassword: value }))}
                      />
                      <PasswordInput
                        label={t('New Password')}
                        value={passwordForm.newPassword}
                        disabled={!accountAvailable}
                        onChange={(value) => setPasswordForm((prev) => ({ ...prev, newPassword: value }))}
                      />
                      <PasswordInput
                        label={t('Confirm New Password')}
                        value={passwordForm.confirmPassword}
                        disabled={!accountAvailable}
                        onChange={(value) => setPasswordForm((prev) => ({ ...prev, confirmPassword: value }))}
                      />
                    </div>

                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {t('Use at least 8 characters. Your current password is verified before updating the account password.')}
                    </p>

                    {passwordError ? <StatusMessage tone="error" text={passwordError} /> : null}
                    {passwordMessage ? <StatusMessage tone="success" text={passwordMessage} /> : null}

                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleChangePassword}
                        disabled={!accountAvailable || isChangingPassword}
                        className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
                      >
                        {isChangingPassword ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                        {t('Update Password')}
                      </button>
                      {passwordMessage && onLogout ? (
                        <button
                          type="button"
                          onClick={onLogout}
                          className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                        >
                          <LogOut size={16} />
                          {t('Sign out now')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PasswordInput: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, disabled, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 pr-10 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300"
        />
        <button
          type="button"
          onClick={() => setVisible((next) => !next)}
          disabled={disabled}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-200/60 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
};

const TextInput: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, disabled, onChange }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">{label}</label>
    <input
      type="text"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-800 dark:bg-[#111111] dark:text-zinc-300"
    />
  </div>
);

const InfoSection: React.FC<{
  title: string;
  rows: Array<{ label: string; value?: string }>;
  emptyText: string;
}> = ({ title, rows, emptyText }) => (
  <section className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-4 dark:border-zinc-800 dark:bg-[#151515]">
    <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
    <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
      {rows.map((row) => (
        <div key={row.label} className="min-w-0">
          <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{row.label}</div>
          <div className="mt-1 truncate text-zinc-800 dark:text-zinc-200">
            {row.value || emptyText}
          </div>
        </div>
      ))}
    </div>
  </section>
);

const StatusMessage: React.FC<{ tone: 'success' | 'error'; text: string }> = ({ tone, text }) => (
  <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${
    tone === 'success'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
      : 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'
  }`}>
    {tone === 'success' ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
    <span>{text}</span>
  </div>
);
